import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Divider,
  Button,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TablePagination,
  Card,
  Avatar,
  Rating,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { userApiClient } from '../../userApiClient';
import { useNavigate } from 'react-router-dom';
import { PaymentStatus, RoomBooking, RoomBookingStatus } from '../../models/RoomBooking';
import { RoomBookingChangeProposal, RoomBookingChangeStatus, DeclineChangeRequest } from '../../models/RoomBookingChange';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import BookingCancellationPolicy from '../../components/BookingCancellationPolicy';
import BookingCreatedAtLabel from '../../components/BookingCreatedAtLabel';
import { useThemeContext } from '../../contexts/ThemeContext';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';
import PassBookingChip from '../../components/passes/PassBookingChip';
import { notifyRoomBookingNeedsActionCountChanged } from '../../utils/roomBookingNeedsActionCount';
import { getTutorRefundStatusChipLabel } from '../../utils/tutorBookingPayment';
import { shouldShowCancellationPolicyForPayment } from '../../utils/formatCancellationPolicy';
import {
  groupUserRoomBookings,
  userRoomCancelReleasesSeries,
} from '../../utils/userRoomBookingGrouping';
import {
  UserRoomSeriesMobileCard,
  UserRoomSeriesTableRow,
} from '../../components/user/UserRoomSeriesBookingCard';

const USER_BOOKINGS_MAX_WIDTH = 1200;

const bookingListImageUrl = (booking: RoomBooking) =>
  booking.spaceImageUrl?.trim() || booking.roomImageUrl?.trim() || undefined;

const BookingListThumbnail = ({
  booking,
  size,
}: {
  booking: RoomBooking;
  size: number;
}) => (
  <Avatar
    src={bookingListImageUrl(booking)}
    alt=""
    variant="rounded"
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      bgcolor: 'action.hover',
      color: 'text.secondary',
      fontSize: size >= 44 ? '0.875rem' : '0.75rem',
      fontWeight: 600,
    }}
  >
    {tutorAvatarInitials(booking.spaceName)}
  </Avatar>
);

// Status chip colors
const statusColors = {
  PENDING_CONFIRMATION: 'warning',
  CONFIRMED_BY_HOST: 'success',
  AUTO_CONFIRMED: 'success',
  AUTO_CONFIRMED_PENDING_PAYMENT: 'success',
  CANCELLED_BY_USER: 'default',
  CANCELLED_BY_HOST: 'error',
  PENDING_PAYMENT: 'error',
  CANCELLED_DUE_AMOUNT_BLOCK_EXPIRATION: 'error',
  CANCELLED_DUE_NO_RESPONSE_FROM_HOST: 'error',
} as const;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bookings-tabpanel-${index}`}
      aria-labelledby={`bookings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function MyBookingsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { mode } = useThemeContext();
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [seriesCancelTargets, setSeriesCancelTargets] = useState<RoomBooking[] | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<RoomBooking | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [inProgressBookings, setInProgressBookings] = useState<RoomBooking[]>([]);
  const [confirmedBookings, setConfirmedBookings] = useState<RoomBooking[]>([]);
  const [cancelledBookings, setCancelledBookings] = useState<RoomBooking[]>([]);
  const [pastBookings, setPastBookings] = useState<RoomBooking[]>([]);
  const [inProgressTotalElements, setInProgressTotalElements] = useState(0);
  const [confirmedTotalElements, setConfirmedTotalElements] = useState(0);
  const [cancelledTotalElements, setCancelledTotalElements] = useState(0);
  const [pastTotalElements, setPastTotalElements] = useState(0);
  const [inProgressTotalPages, setInProgressTotalPages] = useState(0);
  const [confirmedTotalPages, setConfirmedTotalPages] = useState(0);
  const [cancelledTotalPages, setCancelledTotalPages] = useState(0);
  const [pastTotalPages, setPastTotalPages] = useState(0);

  // Review-related state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState<number>(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewBookingDetails, setReviewBookingDetails] = useState<{spaceId: string, spaceName: string} | null>(null);
  const [reviewedSpaceIds, setReviewedSpaceIds] = useState<Set<string>>(new Set());
  const [currentTime, setCurrentTime] = useState(new Date());
  const [copiedPasscodeBookingId, setCopiedPasscodeBookingId] = useState<string | null>(null);

  // Change proposal state
  const [processingChange, setProcessingChange] = useState<string | null>(null);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [selectedChangeProposal, setSelectedChangeProposal] = useState<{
    bookingId: string;
    changeId: string;
    recurringSeriesId?: string | null;
  } | null>(null);

  // Update current time to check if booking has ended (UI only — must not refetch bookings)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all bookings (both upcoming and past)
      const [upcomingResponse, pastResponse] = await Promise.all([
        userApiClient.getRoomBookings(false, page, rowsPerPage),
        userApiClient.getRoomBookings(true, page, rowsPerPage)
      ]);

      const allBookings = [...upcomingResponse.content, ...pastResponse.content];
      const now = new Date();

      // Categorize bookings
      const inProgress: RoomBooking[] = [];
      const confirmed: RoomBooking[] = [];
      const cancelled: RoomBooking[] = [];
      const past: RoomBooking[] = [];

      allBookings.forEach(booking => {
        const startTime = new Date(booking.startTime);
        const isPast = startTime < now;

        // In Progress: Pending Confirmation and not past
        if (booking.status === RoomBookingStatus.PENDING_CONFIRMATION && !isPast) {
          inProgress.push(booking);
        }
        // Confirmed: Confirmed by Host or Auto Confirmed, and not past
        else if ((booking.status === RoomBookingStatus.CONFIRMED_BY_HOST || 
                  booking.status === RoomBookingStatus.AUTO_CONFIRMED ||
                  booking.status === RoomBookingStatus.AUTO_CONFIRMED_PENDING_PAYMENT) && !isPast) {
          confirmed.push(booking);
        }
        // Cancelled: Cancelled by User or Host, and not past
        else if ((booking.status === RoomBookingStatus.CANCELLED_BY_USER || 
                  booking.status === RoomBookingStatus.CANCELLED_BY_HOST) && !isPast) {
          cancelled.push(booking);
        }
        // Past: All bookings that are in the past
        else if (isPast) {
          past.push(booking);
        }
      });

      // Sort bookings by creation date in descending order (newest first)
      const sortedInProgress = [...inProgress].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : new Date(a.startTime).getTime();
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : new Date(b.startTime).getTime();
        return dateB - dateA; // Descending order (newest first)
      });
      const sortedConfirmed = [...confirmed].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : new Date(a.startTime).getTime();
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : new Date(b.startTime).getTime();
        return dateB - dateA; // Descending order (newest first)
      });
      const sortedCancelled = [...cancelled].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : new Date(a.startTime).getTime();
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : new Date(b.startTime).getTime();
        return dateB - dateA; // Descending order (newest first)
      });
      const sortedPast = [...past].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : new Date(a.startTime).getTime();
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : new Date(b.startTime).getTime();
        return dateB - dateA; // Descending order (newest first)
      });

      setInProgressBookings(sortedInProgress);
      setConfirmedBookings(sortedConfirmed);
      setCancelledBookings(sortedCancelled);
      setPastBookings(sortedPast);
      setInProgressTotalElements(inProgress.length);
      setConfirmedTotalElements(confirmed.length);
      setCancelledTotalElements(cancelled.length);
      setPastTotalElements(past.length);
      setInProgressTotalPages(Math.ceil(inProgress.length / rowsPerPage));
      setConfirmedTotalPages(Math.ceil(confirmed.length / rowsPerPage));
      setCancelledTotalPages(Math.ceil(cancelled.length / rowsPerPage));
      setPastTotalPages(Math.ceil(past.length / rowsPerPage));
      
      // Check existing space reviews for past completed bookings (one review per user/space)
      {
        const allPastBookings = [
          ...past,
          ...confirmed.filter(b => new Date(b.startTime) < now),
          ...cancelled.filter(b => new Date(b.startTime) < now)
        ];
        const reviewedSpaceIdsSet = new Set<string>();
        const checkedSpaceIds = new Set<string>();

        for (const booking of allPastBookings) {
          const bookingEndTime = new Date(booking.endTime);
          if (bookingEndTime < now &&
            (booking.status === RoomBookingStatus.CONFIRMED_BY_HOST ||
              booking.status === RoomBookingStatus.AUTO_CONFIRMED ||
              booking.status === RoomBookingStatus.AUTO_CONFIRMED_PENDING_PAYMENT)) {
            if (!booking.spaceId || checkedSpaceIds.has(booking.spaceId)) {
              continue;
            }
            checkedSpaceIds.add(booking.spaceId);
            try {
              const spaceReview = await userApiClient.getSpaceReview(booking.spaceId);
              if (spaceReview) {
                reviewedSpaceIdsSet.add(booking.spaceId);
              }
            } catch (error) {
              console.log('Error fetching space review for space:', booking.spaceId, error);
            }
          }
        }

        setReviewedSpaceIds(reviewedSpaceIdsSet);
      }
      
      setError(null);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, t]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Calculate duration in minutes
  const calculateDuration = (startTime: string, endTime: string) => {
    // Add 'Z' to ensure proper UTC parsing
    const start = new Date(startTime.endsWith('Z') ? startTime : startTime + 'Z');
    const end = new Date(endTime.endsWith('Z') ? endTime : endTime + 'Z');
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
  };

  // Format duration
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${remainingMinutes} ${t('roomBookingRequests.min')}`;
    } else if (remainingMinutes === 0) {
      return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')}`;
    } else {
      return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')} ${remainingMinutes} ${t('roomBookingRequests.min')}`;
    }
  };

  // Format date and time with duration
  const formatDateTimeWithDuration = (startTime: string, endTime: string) => {
    if (!startTime) return '';
    
    const date = new Date(startTime);
    const dateTimeStr = date.toLocaleString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const duration = formatDuration(calculateDuration(startTime, endTime));
    return `${dateTimeStr} (${duration})`;
  };

  // Format date and time with duration for UTC times (e.g., change proposals)
  const formatDateTimeWithDurationUTC = (startTime: string, endTime: string) => {
    if (!startTime) return '';
    
    // Parse as UTC (backend sends UTC times without 'Z')
    const date = new Date(startTime.endsWith('Z') ? startTime : startTime + 'Z');
    const dateTimeStr = date.toLocaleString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const duration = formatDuration(calculateDuration(startTime, endTime));
    return `${dateTimeStr} (${duration})`;
  };

  const handleCopyPasscode = async (passcode: string | null | undefined, bookingId: string) => {
    if (!passcode) return;
    try {
      await navigator.clipboard.writeText(passcode);
      setCopiedPasscodeBookingId(bookingId);
      window.setTimeout(() => {
        setCopiedPasscodeBookingId((current) => (current === bookingId ? null : current));
      }, 1500);
    } catch (copyError) {
      console.error('Failed to copy passcode:', copyError);
    }
  };

  const renderPasscodePill = (booking: RoomBooking) => {
    if (!booking.passcodeInfo) {
      return null;
    }

    const passcode = booking.passcodeInfo.passcode || t('bookingDetails.smartLockPasscodeUnavailable', 'Unavailable');
    const isCopied = copiedPasscodeBookingId === booking.roomBookingId;

    return (
      <Box
        sx={{
          mt: 0.75,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          pl: 1,
          pr: 0.15,
          py: 0.4,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(15,23,42,0.04)',
          width: 'fit-content',
          maxWidth: 'fit-content',
        }}
      >
        <VpnKeyOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 700,
            letterSpacing: '0.08em',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          {passcode}
        </Typography>
        {booking.passcodeInfo.passcode && (
          <Tooltip title={isCopied ? t('bookingDetails.passcodeCopied', 'Copied') : t('bookingDetails.copyPasscode', 'Copy passcode')}>
            <IconButton
              size="small"
              onClick={() => handleCopyPasscode(booking.passcodeInfo?.passcode, booking.roomBookingId)}
              aria-label={t('bookingDetails.copyPasscode', 'Copy passcode')}
              sx={{
                p: 0.25,
                width: 20,
                height: 20,
                minWidth: 20,
                color: isCopied ? 'success.main' : 'text.secondary',
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  // Calculate new end time based on new start time and original duration
  const calculateNewEndTime = (newStartTime: string, originalStartTime: string, originalEndTime: string) => {
    const duration = calculateDuration(originalStartTime, originalEndTime);
    const newStart = new Date(newStartTime.endsWith('Z') ? newStartTime : newStartTime + 'Z');
    const newEnd = new Date(newStart.getTime() + duration * 60 * 1000);
    return newEnd.toISOString();
  };

  const parseProposalInstant = (iso: string) =>
    new Date(iso.endsWith('Z') ? iso : `${iso}Z`);

  /** Use proposal snapshot fields — booking.startTime may already equal newStartTime while oldStartTime differs. */
  const proposalTimeChanged = (proposal: RoomBookingChangeProposal) =>
    parseProposalInstant(proposal.oldStartTime).getTime() !== parseProposalInstant(proposal.newStartTime).getTime();

  const getProposalOldEndIso = (
    proposal: RoomBookingChangeProposal,
    bookingStart: string,
    bookingEnd: string,
  ) => {
    const durMin = calculateDuration(bookingStart, bookingEnd);
    const oldStartMs = parseProposalInstant(proposal.oldStartTime).getTime();
    return new Date(oldStartMs + durMin * 60 * 1000).toISOString();
  };

  const getChangeProposalNewEndIso = (
    proposal: RoomBookingChangeProposal,
    bookingStart: string,
    bookingEnd: string,
  ) => {
    const syntheticOldEnd = getProposalOldEndIso(proposal, bookingStart, bookingEnd);
    return calculateNewEndTime(proposal.newStartTime, proposal.oldStartTime, syntheticOldEnd);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCancelClick = (booking: RoomBooking) => {
    setSeriesCancelTargets(null);
    setSelectedBooking(booking);
    setCancelDialogOpen(true);
  };

  const handleCancelSeriesClick = (bookings: RoomBooking[]) => {
    const held = bookings.find(
      (booking) => booking.paymentStatus === PaymentStatus.AMOUNT_BLOCKED,
    );
    setSeriesCancelTargets(bookings);
    setSelectedBooking(held ?? bookings[0] ?? null);
    setCancelDialogOpen(true);
  };

  const handleCancelBooking = async () => {
    const targets = seriesCancelTargets;
    if (!selectedBooking && !targets?.length) return;

    setCancelling(true);
    try {
      const held = targets?.find(
        (booking) => booking.paymentStatus === PaymentStatus.AMOUNT_BLOCKED,
      );
      if (held) {
        await userApiClient.cancelRoomBooking(held.roomBookingId);
      } else if (targets?.length) {
        for (const booking of targets) {
          await userApiClient.cancelRoomBooking(booking.roomBookingId);
        }
      } else if (selectedBooking) {
        await userApiClient.cancelRoomBooking(selectedBooking.roomBookingId);
      }
      await fetchBookings();
      notifyRoomBookingNeedsActionCountChanged();
      setCancelDialogOpen(false);
      setSeriesCancelTargets(null);
      setError(null);
    } catch (error) {
      console.error('Error cancelling booking:', error);
      setError('Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  // Review-related functions
  const handleOpenReviewDialog = async (booking: RoomBooking) => {
    try {
      if (booking.spaceId) {
        setReviewBookingDetails({
          spaceId: booking.spaceId,
          spaceName: booking.spaceName,
        });
        setReviewDialogOpen(true);
        setReviewError(null);
        return;
      }

      const bookingDetails = await userApiClient.getRoomBookingDetails(booking.roomBookingId);
      setReviewBookingDetails({
        spaceId: bookingDetails.spaceId,
        spaceName: bookingDetails.spaceName,
      });
      setReviewDialogOpen(true);
      setReviewError(null);
    } catch (error) {
      console.error('Error fetching booking details for review:', error);
      setReviewError(t('review.fetchError', 'Failed to load booking details'));
    }
  };

  const handleCreateReview = async () => {
    if (!reviewBookingDetails || reviewRating === 0) return;
    
    setSubmittingReview(true);
    setReviewError(null);
    
    try {
      await userApiClient.createSpaceReview(reviewBookingDetails.spaceId, {
        rating: reviewRating,
        comment: reviewComment.trim() || undefined,
        metadata: { source: 'my_bookings' },
      });
      
      setReviewedSpaceIds(prev => new Set(prev).add(reviewBookingDetails.spaceId));
      
      // Close dialog and reset form
      setReviewDialogOpen(false);
      setReviewRating(0);
      setReviewComment('');
      setReviewBookingDetails(null);
      
      // Refresh bookings to potentially hide the review button
      await fetchBookings();
      
    } catch (error) {
      console.error('Error creating review:', error);
      setReviewError(t('review.createError', 'Failed to create review. Please try again.'));
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleCloseReviewDialog = () => {
    setReviewDialogOpen(false);
    setReviewRating(0);
    setReviewComment('');
    setReviewError(null);
    setReviewBookingDetails(null);
  };

  // Change proposal handlers
  const handleOpenAcceptDialog = (bookingId: string, changeId: string, recurringSeriesId?: string | null) => {
    setSelectedChangeProposal({ bookingId, changeId, recurringSeriesId });
    setAcceptDialogOpen(true);
  };

  const handleAcceptChange = async () => {
    if (!selectedChangeProposal) return;
    const allBookings = [...inProgressBookings, ...confirmedBookings, ...cancelledBookings, ...pastBookings];
    const selectedBooking = allBookings.find((b) => b.roomBookingId === selectedChangeProposal.bookingId);
    if (!selectedBooking || isBookingCancelled(selectedBooking.status)) {
      return;
    }
    
    setProcessingChange(selectedChangeProposal.changeId);
    setError(null);
    try {
      await userApiClient.acceptRoomBookingChange(
        selectedChangeProposal.bookingId,
        selectedChangeProposal.changeId,
        selectedChangeProposal.recurringSeriesId,
      );
      await fetchBookings();
      notifyRoomBookingNeedsActionCountChanged();
      setAcceptDialogOpen(false);
      setSelectedChangeProposal(null);
    } catch (error) {
      console.error('Error accepting change:', error);
      setError(t('bookings.changeProposal.acceptError', 'Failed to accept change proposal'));
    } finally {
      setProcessingChange(null);
    }
  };

  const handleCloseAcceptDialog = () => {
    setAcceptDialogOpen(false);
    setSelectedChangeProposal(null);
  };

  const handleOpenDeclineDialog = (bookingId: string, changeId: string, recurringSeriesId?: string | null) => {
    setSelectedChangeProposal({ bookingId, changeId, recurringSeriesId });
    setDeclineReason('');
    setDeclineDialogOpen(true);
  };

  const handleDeclineChange = async () => {
    if (!selectedChangeProposal) return;
    const allBookings = [...inProgressBookings, ...confirmedBookings, ...cancelledBookings, ...pastBookings];
    const selectedBooking = allBookings.find((b) => b.roomBookingId === selectedChangeProposal.bookingId);
    if (!selectedBooking || isBookingCancelled(selectedBooking.status)) {
      return;
    }
    
    setProcessingChange(selectedChangeProposal.changeId);
    setError(null);
    try {
      const request: DeclineChangeRequest = {
        reason: declineReason.trim() || undefined
      };
      await userApiClient.declineRoomBookingChange(
        selectedChangeProposal.bookingId,
        selectedChangeProposal.changeId,
        request,
        selectedChangeProposal.recurringSeriesId,
      );
      await fetchBookings();
      notifyRoomBookingNeedsActionCountChanged();
      setDeclineDialogOpen(false);
      setSelectedChangeProposal(null);
      setDeclineReason('');
    } catch (error) {
      console.error('Error declining change:', error);
      setError(t('bookings.changeProposal.declineError', 'Failed to decline change proposal'));
    } finally {
      setProcessingChange(null);
    }
  };

  const handleCloseDeclineDialog = () => {
    setDeclineDialogOpen(false);
    setSelectedChangeProposal(null);
    setDeclineReason('');
  };

  // Check if a booking can have a review written
  const canWriteReview = (booking: RoomBooking) => {
    const allBookings = [...inProgressBookings, ...confirmedBookings, ...cancelledBookings, ...pastBookings];
    const seriesId = booking.recurringSeriesId;

    // Single booking: must be completed + confirmed.
    // Recurring series: allow review only after ALL occurrences are completed + confirmed.
    if (seriesId) {
      const seriesBookings = allBookings.filter((b) => b.recurringSeriesId === seriesId);

      // Avoid showing multiple review buttons for the same series.
      if (!isLastBookingInSeries(booking, seriesBookings)) return false;

      const allSeriesCompletedAndConfirmed = seriesBookings.every(
        (b) => bookingCompleted(b) && isBookingConfirmedStatus(b.status),
      );
      if (!allSeriesCompletedAndConfirmed) return false;
    } else {
      if (!bookingCompleted(booking) || !isBookingConfirmedStatus(booking.status)) return false;
    }

    // One review per space — hide write CTA once the space has been reviewed
    if (!booking.spaceId) {
      return false;
    }

    return !reviewedSpaceIds.has(booking.spaceId);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format payment amount with currency
  const formatPaymentAmount = (amount: number, currencyCode: string) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  // Get payment status color
  const getPaymentStatusColor = (status: string | null | undefined) => {
    if (!status) return 'default';
    
    switch (status) {
      case 'PAID':
        return 'success';
      case 'AMOUNT_BLOCKED':
        return 'warning';
      case 'BLOCK_REQUESTED':
        return 'info';
      case 'TO_BE_PAID_ON_SITE':
        return 'default';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get refund status color
  const getRefundStatusColor = (status: string | null | undefined) => {
    if (!status) return 'default';
    
    switch (status) {
      case 'SUCCEEDED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'FAILED':
      case 'CANCELED':
        return 'error';
      case 'REQUIRES_ACTION':
        return 'info';
      default:
        return 'default';
    }
  };

  // Get payment method icon
  const getPaymentMethodIcon = (method: string) => {
    switch (method?.toUpperCase()) {
      case 'CASH':
        return <AttachMoneyIcon fontSize="small" />;
      case 'ONLINE':
        return <CreditCardIcon fontSize="small" />;
      default:
        return null;
    }
  };

  // Get change proposal status description
  const getChangeProposalStatusDescription = (status: RoomBookingChangeStatus): string => {
    switch (status) {
      case RoomBookingChangeStatus.PENDING_REPLY:
        return t('bookings.changeProposalStatus.pending_reply', 'Pending Reply');
      case RoomBookingChangeStatus.ACCEPTED:
        return t('bookings.changeProposalStatus.accepted', 'Accepted');
      case RoomBookingChangeStatus.DECLINED:
        return t('bookings.changeProposalStatus.declined', 'Declined');
      case RoomBookingChangeStatus.CANCELLED:
        return t('bookings.changeProposalStatus.cancelled', 'Cancelled');
      case RoomBookingChangeStatus.EXPIRED:
        return t('bookings.changeProposalStatus.expired', 'Expired');
      default:
        return status;
    }
  };

  // Get change proposal status color
  const getChangeProposalStatusColor = (status: RoomBookingChangeStatus) => {
    switch (status) {
      case RoomBookingChangeStatus.PENDING_REPLY:
        return 'warning';
      case RoomBookingChangeStatus.ACCEPTED:
        return 'success';
      case RoomBookingChangeStatus.DECLINED:
        return 'error';
      case RoomBookingChangeStatus.CANCELLED:
        return 'default';
      case RoomBookingChangeStatus.EXPIRED:
        return 'default';
      default:
        return 'default';
    }
  };

  const getChangeProposalAutoAcceptAlert = (status: RoomBookingChangeStatus) => {
    switch (status) {
      case RoomBookingChangeStatus.PENDING_REPLY:
        return {
          severity: 'info' as const,
          message: t('bookings.changeProposal.autoAccept', 'Booking will be automatically approved if accepted'),
        };
      case RoomBookingChangeStatus.ACCEPTED:
        return {
          severity: 'success' as const,
          message: t(
            'bookings.changeProposal.autoAcceptAccepted',
            'When you accepted, the booking was confirmed automatically, as the host requested.',
          ),
        };
      case RoomBookingChangeStatus.DECLINED:
        return {
          severity: 'warning' as const,
          message: t(
            'bookings.changeProposal.autoAcceptDeclined',
            'The host had automatic confirmation on accept turned on—you declined, so that did not apply.',
          ),
        };
      default:
        return {
          severity: 'info' as const,
          message: t(
            'bookings.changeProposal.autoAcceptClosed',
            'This proposal included automatic confirmation if accepted; it no longer applies.',
          ),
        };
    }
  };

  const getChangeProposalReason = (proposal: RoomBookingChangeProposal): string | null => {
    const candidates = [proposal.proposalReason, proposal.reason, proposal.replyComment];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim();
      }
    }
    return null;
  };

  // Helper function to check if booking is cancelled
  const isBookingCancelled = (status: RoomBookingStatus): boolean => {
    return status === RoomBookingStatus.CANCELLED_BY_USER ||
           status === RoomBookingStatus.CANCELLED_BY_HOST ||
           status === RoomBookingStatus.CANCELLED_DUE_AMOUNT_BLOCK_EXPIRATION ||
           status === RoomBookingStatus.CANCELLED_DUE_NO_RESPONSE_FROM_HOST;
  };

  const isBookingConfirmedStatus = (status: RoomBookingStatus): boolean => {
    return (
      status === RoomBookingStatus.CONFIRMED_BY_HOST ||
      status === RoomBookingStatus.AUTO_CONFIRMED ||
      status === RoomBookingStatus.AUTO_CONFIRMED_PENDING_PAYMENT
    );
  };

  const bookingCompleted = (booking: RoomBooking): boolean => {
    const bookingEndTime = new Date(booking.endTime);
    const currentTimeUTC = new Date(currentTime.toISOString());
    return bookingEndTime < currentTimeUTC;
  };

  const isLastBookingInSeries = (booking: RoomBooking, allSeriesBookings: RoomBooking[]): boolean => {
    if (!booking.recurringSeriesId) return true;
    if (allSeriesBookings.length === 0) return false;

    const bookingEnd = new Date(booking.endTime).getTime();
    const maxEnd = Math.max(...allSeriesBookings.map((b) => new Date(b.endTime).getTime()));
    if (bookingEnd !== maxEnd) return false;

    const lastBookings = allSeriesBookings
      .filter((b) => new Date(b.endTime).getTime() === maxEnd)
      .sort((a, b) => a.roomBookingId.localeCompare(b.roomBookingId));
    return lastBookings[0]?.roomBookingId === booking.roomBookingId;
  };

  // Get current tab's bookings and metadata
  const getCurrentTabData = () => {
    switch (tabValue) {
      case 0: // In Progress
        return {
          bookings: inProgressBookings,
          totalElements: inProgressTotalElements,
          totalPages: inProgressTotalPages,
          isPast: false
        };
      case 1: // Confirmed
        return {
          bookings: confirmedBookings,
          totalElements: confirmedTotalElements,
          totalPages: confirmedTotalPages,
          isPast: false
        };
      case 2: // Cancelled
        return {
          bookings: cancelledBookings,
          totalElements: cancelledTotalElements,
          totalPages: cancelledTotalPages,
          isPast: false
        };
      case 3: // Past
        return {
          bookings: pastBookings,
          totalElements: pastTotalElements,
          totalPages: pastTotalPages,
          isPast: true
        };
      default:
        return {
          bookings: inProgressBookings,
          totalElements: inProgressTotalElements,
          totalPages: inProgressTotalPages,
          isPast: false
        };
    }
  };

  const renderBookingsTable = (bookingsToShow: RoomBooking[], isPast: boolean) => {
    if (bookingsToShow.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {tabValue === 0 && t('bookings.noInProgressBookings')}
            {tabValue === 1 && t('bookings.noConfirmedBookings')}
            {tabValue === 2 && t('bookings.noCancelledBookings')}
            {tabValue === 3 && t('bookings.noPastBookings')}
          </Typography>
          {tabValue !== 3 && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => navigate('/user/search')}
              sx={{ mt: 2 }}
            >
              {t('bookings.browseRooms')}
            </Button>
          )}
        </Paper>
      );
    }

    const displayRows = groupUserRoomBookings(bookingsToShow, currentTime);
    const seriesCardProps = {
      onOpen: (bookingId: string) => navigate(`/user/bookings/${bookingId}`),
      formatWhen: formatDateTimeWithDuration,
      formatAmount: formatPaymentAmount,
    };

    return (
      <Box>
        {/* Mobile Card View */}
        <Box sx={{ display: { xs: 'block', sm: 'none' }, px: 1 }}>
          <Stack spacing={2.5}>
            {displayRows.map((entry) => {
              if (entry.kind === 'series') {
                return (
                  <UserRoomSeriesMobileCard
                    key={entry.seriesId}
                    bookings={entry.bookings}
                    onCancel={() => handleCancelSeriesClick(entry.bookings)}
                    {...seriesCardProps}
                  />
                );
              }
              const booking = entry.booking;
              return (
              <Card 
                key={booking.roomBookingId} 
                elevation={2}
                sx={{ 
                  p: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    elevation: 4,
                    borderColor: 'primary.light',
                    transform: 'translateY(-1px)',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                    boxShadow: (theme) => `0 4px 12px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`
                  }
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip 
                      label={t(`status.${booking.status.toLowerCase()}`, booking.status)}
                      color={statusColors[booking.status as keyof typeof statusColors] || 'default'} 
                      size="medium"
                      sx={{
                        flex: '1 1 auto',
                        fontWeight: 600,
                        '& .MuiChip-label': {
                          fontSize: '0.875rem',
                          textAlign: 'center'
                        }
                      }}
                    />
                  </Stack>

                  {/* Header with Space and Room */}
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <BookingListThumbnail booking={booking} size={44} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {booking.spaceName}
                    </Typography>
                    {(() => {
                      const roomChanged = booking.changeProposal && 
                        booking.changeProposal.newRoomId !== booking.changeProposal.oldRoomId && 
                        booking.changeProposal.newRoomName;
                      const roomName = roomChanged && booking.changeProposal?.newRoomName 
                        ? booking.changeProposal.newRoomName 
                        : booking.roomName;
                      
                      return (
                        <Box>
                          {roomChanged && booking.changeProposal?.newRoomName && (
                            <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontSize: '0.7rem' }}>
                              {t('bookings.changeProposal.newRoom', 'New Room')}
                            </Typography>
                          )}
                          <Typography 
                            variant="body2" 
                            color={roomChanged ? "info.main" : "text.secondary"}
                            fontWeight={roomChanged ? 600 : undefined}
                          >
                            {roomName}
                          </Typography>
                          {renderPasscodePill(booking)}
                        </Box>
                      );
                    })()}
                    </Box>
                  </Stack>

                  {/* Date, Time and Duration */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {(() => {
                      // If change is accepted, show the new time from the proposal
                      if (booking.changeProposal && 
                          booking.changeProposal.status === RoomBookingChangeStatus.ACCEPTED) {
                        const newEndTime = getChangeProposalNewEndIso(
                          booking.changeProposal,
                          booking.startTime,
                          booking.endTime
                        );
                        return (
                          <Typography variant="body2">
                            {formatDateTimeWithDurationUTC(booking.changeProposal.newStartTime, newEndTime)}
                          </Typography>
                        );
                      }
                      return (
                        <Typography variant="body2">
                          {formatDateTimeWithDuration(booking.startTime, booking.endTime)}
                        </Typography>
                      );
                    })()}
                    {!isPast &&
                    !isBookingCancelled(booking.status) &&
                    shouldShowCancellationPolicyForPayment(booking.paymentStatus) ? (
                      <BookingCancellationPolicy
                        startTime={booking.startTime}
                        timezone={booking.timezone}
                        policy={booking.cancellationPolicy}
                        showWhenMissing={false}
                      />
                    ) : null}
                  </Box>

                  {/* Change Proposal Section (include declined/cancelled/expired so users still see what was proposed) */}
                  {booking.changeProposal && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Box 
                        sx={{ 
                          p: 1.5, 
                          borderRadius: 1,
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(79, 95, 235, 0.1)' : 'rgba(79, 95, 235, 0.05)',
                          border: '1px solid',
                          borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(79, 95, 235, 0.3)' : 'rgba(79, 95, 235, 0.2)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="caption" fontWeight="bold" color="info.main">
                            {t('bookings.changeProposal.title', 'Change Proposal')}
                          </Typography>
                          <Chip 
                            label={getChangeProposalStatusDescription(booking.changeProposal.status)}
                            color={getChangeProposalStatusColor(booking.changeProposal.status) as any}
                            size="small"
                          />
                        </Box>
                        {(() => {
                          // Check if room changed
                          const roomChanged = booking.changeProposal.newRoomId !== booking.changeProposal.oldRoomId && 
                            booking.changeProposal.newRoomName;
                          
                          const timeChanged = proposalTimeChanged(booking.changeProposal);
                          const isAccepted = booking.changeProposal.status === RoomBookingChangeStatus.ACCEPTED;
                          const proposalReason = getChangeProposalReason(booking.changeProposal);
                          
                          return (
                            <Box sx={{ mb: 1.5 }}>
                              {roomChanged && (
                                <>
                                  <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                                    {isAccepted ? t('bookings.changeProposal.newRoom', 'New Room') : t('bookings.changeProposal.newRoom', 'New Room')}
                                  </Typography>
                                  <Typography variant="body2" fontWeight="600" color="info.main" sx={{ mb: 1 }}>
                                    {booking.changeProposal.newRoomName}
                                  </Typography>
                                </>
                              )}
                              {timeChanged ? (
                                <>
                                  <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                                    {t('bookings.changeProposal.newTime', 'New Time')}
                                  </Typography>
                                  <Typography variant="body2" fontWeight="600">
                                    {formatDateTimeWithDurationUTC(booking.changeProposal.newStartTime, getChangeProposalNewEndIso(booking.changeProposal, booking.startTime, booking.endTime))}
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="body2">
                                  {formatDateTimeWithDuration(booking.startTime, booking.endTime)}
                                </Typography>
                              )}
                              {proposalReason && (
                                <>
                                  <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ display: 'block', mb: 0.25, mt: 1 }}>
                                    {t('bookings.changeProposal.proposalReason', 'Proposal reason')}
                                  </Typography>
                                  <Typography variant="body2">{proposalReason}</Typography>
                                </>
                              )}
                            </Box>
                          );
                        })()}
                        {booking.changeProposal.autoAcceptRoomBooking && (() => {
                          const { severity, message } = getChangeProposalAutoAcceptAlert(booking.changeProposal.status);
                          return (
                            <Alert
                              severity={severity}
                              sx={{
                                mt: 1,
                                py: 0.5,
                                '& .MuiAlert-message': { width: '100%' },
                              }}
                            >
                              <Typography variant="body2" fontSize="0.8125rem">
                                {message}
                              </Typography>
                            </Alert>
                          );
                        })()}
                        {booking.changeProposal.status === RoomBookingChangeStatus.PENDING_REPLY &&
                          !isBookingCancelled(booking.status) && (
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={() => handleOpenAcceptDialog(booking.roomBookingId, booking.changeProposal!.id, booking.recurringSeriesId)}
                              disabled={processingChange === booking.changeProposal!.id}
                              startIcon={
                                processingChange === booking.changeProposal!.id ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : undefined
                              }
                              sx={{ flex: 1 }}
                            >
                              {t('bookings.changeProposal.accept', 'Accept')}
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleOpenDeclineDialog(booking.roomBookingId, booking.changeProposal!.id, booking.recurringSeriesId)}
                              disabled={processingChange === booking.changeProposal!.id}
                              sx={{ flex: 1 }}
                            >
                              {t('bookings.changeProposal.decline', 'Decline')}
                            </Button>
                          </Stack>
                        )}
                      </Box>
                    </>
                  )}

                  <Divider />

                  {/* Payment Section */}
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                        {t('bookings.payment')}
                      </Typography>
                      {/* Payment Status — hidden when covered by pass */}
                      {!booking.pass && !booking.stripeRefundStatus && booking.paymentStatus && (
                        <Chip 
                          label={t(`paymentStatus.${booking.paymentStatus.toLowerCase()}`)}
                          color={getPaymentStatusColor(booking.paymentStatus) as any}
                          size="small" 
                        />
                      )}
                      {/* Refund Status */}
                      {booking.stripeRefundStatus && (
                        <Chip 
                          icon={<ReceiptIcon fontSize="small" />}
                          label={getTutorRefundStatusChipLabel(booking.stripeRefundStatus, t)}
                          color={getRefundStatusColor(booking.stripeRefundStatus) as any}
                          size="small" 
                        />
                      )}
                      {booking.pass && <PassBookingChip pass={booking.pass} />}
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      bgcolor: 'background.default',
                      p: 1.5,
                      borderRadius: 1,
                    }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color={booking.pass ? 'text.secondary' : 'text.primary'}
                        sx={{
                          fontSize: '1.125rem',
                          textDecoration: booking.pass ? 'line-through' : 'none',
                        }}
                      >
                        {formatPaymentAmount(booking.totalAmount, booking.currencyCode)}
                      </Typography>
                      {!booking.pass && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getPaymentMethodIcon(booking.paymentMethod)}
                          <Typography variant="body2" color="text.secondary">
                            {booking.paymentMethod ? t(`paymentMethod.${booking.paymentMethod.toLowerCase()}`) : t('paymentMethod.unknown')}
                          </Typography>
                        </Box>
                      )}
                      </Box>
                  </Box>

                  <BookingCreatedAtLabel
                    createdAt={booking.createdAt}
                    timezone={booking.timezone}
                    placement="footer"
                  />

                  <Divider />

                  {/* Actions */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end',
                    gap: 1,
                    mt: 1
                  }}>
                    {canWriteReview(booking) && (
                      <Button 
                        variant="outlined" 
                        color="primary" 
                        size="small"
                        onClick={() => handleOpenReviewDialog(booking)}
                      >
                        {t('bookingDetails.writeReview', 'Write Review')}
                      </Button>
                    )}
                    <Button 
                      variant="contained" 
                      size="small"
                      onClick={() => navigate(`/user/bookings/${booking.roomBookingId}`)}
                    >
                      {t('common.details')}
                    </Button>
{!isBookingCancelled(booking.status) && !isPast && (tabValue === 0 || tabValue === 1) && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleCancelClick(booking)}
                        disabled={new Date(booking.endTime) < new Date()}
                      >
                        {t('common.cancel')}
                      </Button>
                    )}
                  </Box>
                </Stack>
              </Card>
              );
            })}
          </Stack>
        </Box>

        {/* Desktop Table View */}
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('bookings.space')}</TableCell>
                  <TableCell>{t('bookings.room')}</TableCell>
                  <TableCell>{t('bookings.dateTime')}</TableCell>
                  <TableCell>{t('bookings.payment')}</TableCell>
                  <TableCell>{t('bookings.status')}</TableCell>
                  <TableCell>{t('bookings.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.map((entry, index) => {
                  if (entry.kind === 'series') {
                    return (
                      <UserRoomSeriesTableRow
                        key={entry.seriesId}
                        bookings={entry.bookings}
                        showDivider={index < displayRows.length - 1}
                        onCancel={() => handleCancelSeriesClick(entry.bookings)}
                        {...seriesCardProps}
                      />
                    );
                  }
                  const booking = entry.booking;
                  return (
                  <React.Fragment key={booking.roomBookingId}>
                    {/* Main Booking Row */}
                    <TableRow
                      sx={{
                        '& td': {
                          borderBottom: 'none',
                          verticalAlign: 'middle',
                          pt: index === 0 ? 1 : 1,
                          pb: booking.changeProposal && booking.changeProposal.status === RoomBookingChangeStatus.PENDING_REPLY ? 0.5 : 1,
                        }
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <BookingListThumbnail booking={booking} size={36} />
                          <Typography variant="body2">{booking.spaceName}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">{booking.roomName}</Typography>
                          {renderPasscodePill(booking)}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.75}>
                          <Typography variant="body2">
                            {formatDateTimeWithDuration(booking.startTime, booking.endTime)}
                          </Typography>
                          {!isPast &&
                          !isBookingCancelled(booking.status) &&
                          shouldShowCancellationPolicyForPayment(booking.paymentStatus) ? (
                            <BookingCancellationPolicy
                              startTime={booking.startTime}
                              timezone={booking.timezone}
                              policy={booking.cancellationPolicy}
                              showWhenMissing={false}
                            />
                          ) : null}
                          <BookingCreatedAtLabel
                            createdAt={booking.createdAt}
                            timezone={booking.timezone}
                            placement="footer"
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color={booking.pass ? 'text.secondary' : 'text.primary'}
                              sx={{ textDecoration: booking.pass ? 'line-through' : 'none' }}
                            >
                              {formatPaymentAmount(booking.totalAmount, booking.currencyCode)}
                            </Typography>
                            {!booking.pass && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {getPaymentMethodIcon(booking.paymentMethod)}
                                <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                  {t(`paymentMethod.${booking.paymentMethod?.toLowerCase() || 'unknown'}`)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {!booking.pass && !booking.stripeRefundStatus && (
                              <Chip 
                                label={t(`paymentStatus.${booking.paymentStatus?.toLowerCase() || 'unknown'}`)}
                                color={getPaymentStatusColor(booking.paymentStatus) as any}
                                size="small" 
                              />
                            )}
                            {booking.stripeRefundStatus && (
                              <Chip
                                icon={<ReceiptIcon fontSize="small" />}
                                label={getTutorRefundStatusChipLabel(booking.stripeRefundStatus, t)}
                                color={getRefundStatusColor(booking.stripeRefundStatus) as any}
                                size="small"
                              />
                            )}
                            {booking.pass && <PassBookingChip pass={booking.pass} />}
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={t(`status.${booking.status.toLowerCase()}`, booking.status)}
                          color={statusColors[booking.status as keyof typeof statusColors] || 'default'} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {canWriteReview(booking) && (
                            <Button 
                              variant="outlined" 
                              color="primary" 
                              size="small"
                              onClick={() => handleOpenReviewDialog(booking)}
                            >
                              {t('bookingDetails.writeReview', 'Write Review')}
                            </Button>
                          )}
                          <Button 
                            variant="contained" 
                            size="small"
                            onClick={() => navigate(`/user/bookings/${booking.roomBookingId}`)}
                          >
                            {t('common.details')}
                          </Button>
{!isBookingCancelled(booking.status) && !isPast && (tabValue === 0 || tabValue === 1) && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleCancelClick(booking)}
                        disabled={new Date(booking.endTime) < new Date()}
                      >
                        {t('common.cancel')}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  {/* Change Proposal Row */}
                  {booking.changeProposal && (
                    <TableRow
                      sx={{
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(79, 95, 235, 0.08)' : 'rgba(79, 95, 235, 0.04)',
                        position: 'relative',
                        '& td': {
                          borderTop: 'none',
                          borderBottom: 'none',
                          verticalAlign: 'middle',
                          py: 0.5,
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={t('bookings.changeProposal.title', 'Change Proposal')}
                            color="info"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                          <Chip 
                            label={getChangeProposalStatusDescription(booking.changeProposal.status)}
                            color={getChangeProposalStatusColor(booking.changeProposal.status) as any}
                            size="small"
                          />
                          {booking.changeProposal.status === RoomBookingChangeStatus.PENDING_REPLY &&
                            !isBookingCancelled(booking.status) && (
                          <Stack direction="row" spacing={0.5} sx={{ ml: 0.5 }}>
                            <Tooltip title={t('bookings.changeProposal.accept', 'Accept')} arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenAcceptDialog(booking.roomBookingId, booking.changeProposal!.id, booking.recurringSeriesId)}
                                disabled={processingChange === booking.changeProposal!.id}
                                sx={{ 
                                  width: '24px',
                                  height: '24px',
                                  p: 0.5,
                                  border: '1px solid',
                                  borderColor: 'success.main',
                                  borderRadius: 1,
                                  bgcolor: 'success.main',
                                  '& svg': {
                                    color: 'white'
                                  },
                                  '&:hover': {
                                    bgcolor: 'success.dark',
                                    borderColor: 'success.dark',
                                  },
                                  '&.Mui-disabled': {
                                    borderColor: 'action.disabled',
                                    bgcolor: 'action.disabledBackground'
                                  }
                                }}
                              >
                                {processingChange === booking.changeProposal!.id ? (
                                  <CircularProgress size={14} sx={{ color: 'white' }} />
                                ) : (
                                  <CheckIcon fontSize="small" sx={{ fontSize: '16px', color: 'white' }} />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('bookings.changeProposal.decline', 'Decline')} arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDeclineDialog(booking.roomBookingId, booking.changeProposal!.id, booking.recurringSeriesId)}
                                disabled={processingChange === booking.changeProposal!.id}
                                sx={{ 
                                  width: '24px',
                                  height: '24px',
                                  p: 0.5,
                                  border: '1px solid',
                                  borderColor: 'error.main',
                                  borderRadius: 1,
                                  bgcolor: 'error.main',
                                  '& svg': {
                                    color: 'white'
                                  },
                                  '&:hover': {
                                    bgcolor: 'error.dark',
                                    borderColor: 'error.dark',
                                  },
                                  '&.Mui-disabled': {
                                    borderColor: 'action.disabled',
                                    bgcolor: 'action.disabledBackground'
                                  }
                                }}
                              >
                                <CloseIcon fontSize="small" sx={{ fontSize: '16px', color: 'white' }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          )}
                        </Box>
                        {(() => {
                          const proposalReason = getChangeProposalReason(booking.changeProposal);
                          if (!proposalReason) return null;
                          return (
                            <Box sx={{ mt: 0.75 }}>
                              <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                {t('bookings.changeProposal.proposalReason', 'Proposal reason')}
                              </Typography>
                              <Typography variant="body2" fontSize="0.8125rem">
                                {proposalReason}
                              </Typography>
                            </Box>
                          );
                        })()}
                        </TableCell>
                      <TableCell>
                        {(() => {
                          const roomChanged = booking.changeProposal.newRoomId !== booking.changeProposal.oldRoomId;
                          const roomName = roomChanged && booking.changeProposal.newRoomName 
                            ? booking.changeProposal.newRoomName 
                            : booking.roomName;
                          
                          return (
                            <Box>
                              {roomChanged && booking.changeProposal.newRoomName && (
                                <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontSize: '0.75rem' }}>
                                  {t('bookings.changeProposal.newRoom', 'New Room')}
                                </Typography>
                              )}
                              <Typography 
                                variant="body2" 
                                fontSize="0.875rem" 
                                fontWeight={roomChanged ? 600 : undefined}
                                color={roomChanged ? "info.main" : undefined}
                              >
                                {roomName}
                              </Typography>
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const timeChanged = proposalTimeChanged(booking.changeProposal!);
                          
                          return (
                            <Box>
                              {timeChanged ? (
                                <>
                                  <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontSize: '0.75rem' }}>
                                    {t('bookings.changeProposal.newTime', 'New Time')}
                                  </Typography>
                                  <Typography variant="body2" fontSize="0.875rem" fontWeight="600">
                                    {formatDateTimeWithDurationUTC(booking.changeProposal!.newStartTime, getChangeProposalNewEndIso(booking.changeProposal!, booking.startTime, booking.endTime))}
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="body2" fontSize="0.875rem">
                                  {formatDateTimeWithDuration(booking.startTime, booking.endTime)}
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      </TableRow>
                    )}
                  {/* Separator row between bookings */}
                  {index < displayRows.length - 1 && (
                    <TableRow>
                      <TableCell 
                        colSpan={6} 
                        sx={{ 
                          height: 8,
                          p: 0,
                          borderTop: '1px solid',
                          borderBottom: 'none',
                          borderColor: 'divider',
                          bgcolor: 'transparent'
                        }}
                      />
                      </TableRow>
                    )}
                  </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={getCurrentTabData().totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('common.pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => 
            t('common.pagination.displayedRows', { from, to, count })
          }
          sx={{
            mt: { xs: 1, sm: 2 },
            '& .MuiTablePagination-toolbar': { px: { xs: 0.5, sm: 2 } },
            '& .MuiTablePagination-selectLabel': { display: { xs: 'none', sm: 'inline' } },
            '& .MuiTablePagination-input': { display: { xs: 'none', sm: 'inline-flex' } },
            '& .MuiTablePagination-displayedRows': { fontSize: { xs: '0.8rem', sm: '0.875rem' } }
          }}
        />
      </Box>
    );
  };

  return (
    <>
      <SEO 
        title={t('bookings.myBookings')}
        noindex={true}
      />
      <Box
        sx={{
          py: { xs: 2, sm: 4 },
          px: { xs: 1, sm: 2 },
          pb: { xs: 8, sm: 10 },
          maxWidth: USER_BOOKINGS_MAX_WIDTH,
          width: '100%',
          mx: 'auto',
        }}
      >
        <Card elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            {t('bookings.myBookings')}
          </Typography>
          <Divider sx={{ mb: { xs: 2, sm: 4 } }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  aria-label="bookings tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                >
                  <Tab label={`${t('bookings.inProgress')} (${inProgressTotalElements})`} />
                  <Tab label={`${t('bookings.confirmed')} (${confirmedTotalElements})`} />
                  <Tab label={`${t('bookings.cancelled')} (${cancelledTotalElements})`} />
                  <Tab label={`${t('bookings.past')} (${pastTotalElements})`} />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                {renderBookingsTable(inProgressBookings, false)}
              </TabPanel>
              <TabPanel value={tabValue} index={1}>
                {renderBookingsTable(confirmedBookings, false)}
              </TabPanel>
              <TabPanel value={tabValue} index={2}>
                {renderBookingsTable(cancelledBookings, false)}
              </TabPanel>
              <TabPanel value={tabValue} index={3}>
                {renderBookingsTable(pastBookings, true)}
              </TabPanel>
            </>
          )}

          <Dialog
            open={cancelDialogOpen}
            onClose={() => {
              if (cancelling) return;
              setCancelDialogOpen(false);
              setSeriesCancelTargets(null);
            }}
          >
            <DialogTitle>
              {seriesCancelTargets?.length ||
              (selectedBooking && userRoomCancelReleasesSeries(selectedBooking))
                ? t('bookings.cancelAllSessionsTitle', 'Cancel all sessions?')
                : t('bookings.cancelBooking')}
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ mb: 1 }}>
                {seriesCancelTargets?.length ||
                (selectedBooking && userRoomCancelReleasesSeries(selectedBooking))
                  ? t(
                      'bookings.cancelOnlineSeriesHold',
                      'These sessions are still waiting for approval. Cancelling releases the card hold for the whole series.',
                    )
                  : t('bookings.cancelConfirmation')}
              </Typography>
              {selectedBooking &&
                !seriesCancelTargets?.length &&
                !userRoomCancelReleasesSeries(selectedBooking) &&
                shouldShowCancellationPolicyForPayment(selectedBooking.paymentStatus) && (
                <BookingCancellationPolicy
                  startTime={selectedBooking.startTime}
                  timezone={selectedBooking.timezone}
                  policy={selectedBooking.cancellationPolicy}
                  showWhenMissing={false}
                  density="comfortable"
                  sx={{ mb: selectedBooking.recurringSeriesId ? 1 : 0, mt: 1 }}
                />
              )}
              {selectedBooking?.recurringSeriesId &&
                !seriesCancelTargets?.length &&
                !userRoomCancelReleasesSeries(selectedBooking) &&
                selectedBooking.paymentMethod === 'ONLINE' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('bookings.cancelSingleOccurrenceInfo', 'This will cancel only this session. A proportional refund will be issued.')}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => {
                  setCancelDialogOpen(false);
                  setSeriesCancelTargets(null);
                }} 
                disabled={cancelling}
              >
                {t('bookings.keepBooking')}
              </Button>
              <Button 
                onClick={handleCancelBooking} 
                color="error" 
                variant="contained"
                disabled={cancelling}
              >
                {cancelling ? t('bookings.cancelling') : t('bookings.confirmCancel')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Review Dialog */}
          <Dialog 
            open={reviewDialogOpen} 
            onClose={handleCloseReviewDialog}
            maxWidth="sm"
            fullWidth
            sx={{
              '& .MuiDialog-paper': {
                borderRadius: 3,
                margin: { xs: 1, sm: 2 },
                maxWidth: { xs: 'calc(100% - 16px)', sm: '600px' },
                boxShadow: mode === 'dark' 
                  ? '0px 8px 32px rgba(0, 0, 0, 0.6)' 
                  : '0px 4px 20px rgba(0, 0, 0, 0.12)',
                background: mode === 'dark' ? '#1F2129' : '#FFFFFF',
                border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
              }
            }}
          >
            {/* Header Section */}
            <Box sx={{ 
              p: { xs: 1.5, md: 2 }, 
              borderBottom: '1px solid',
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
              bgcolor: mode === 'dark' ? '#1F2129' : '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    minWidth: 0,
                    color: mode === 'dark' ? '#ECECFB' : '#212529',
                    letterSpacing: '-0.02em'
                  }}
                  noWrap
                  title={reviewBookingDetails?.spaceName}
                >
                  {reviewBookingDetails?.spaceName}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="overline" sx={{ color: 'grey.400', letterSpacing: 2, display: { xs: 'none', sm: 'block' } }}>
                  {t('review.writeReview', 'REVIEW')}
                </Typography>
                <IconButton aria-label="close" onClick={handleCloseReviewDialog}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Content Section */}
            <DialogContent sx={{ 
              pt: 3, 
              px: 3, 
              pb: 2,
              bgcolor: mode === 'dark' ? '#1F2129' : '#FFFFFF'
            }}>
              <Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3,
                  gap: 2
                }}>
                  <Typography variant="body1" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
                    {t('review.rateYourExperience', 'Rate your experience')}
                  </Typography>
                  <Rating
                    value={reviewRating}
                    onChange={(event, newValue) => {
                      setReviewRating(newValue || 0);
                    }}
                    size="large"
                    sx={{
                      '& .MuiRating-iconFilled': {
                        color: '#4E67EB',
                      },
                      '& .MuiRating-iconHover': {
                        color: '#3d56d9',
                      },
                    }}
                  />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: mode === 'dark' ? '#ECECFB' : '#212529' }}>
                    {reviewRating}/5
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t('review.comment', 'Share your experience (optional)')}
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder={t('review.commentPlaceholder', 'Tell others about your experience with this space...')}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
                      '&:hover': {
                        backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f2ff',
                      },
                      '&.Mui-focused': {
                        backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f2ff',
                      }
                    }
                  }}
                />

                {reviewError && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {reviewError}
                  </Alert>
                )}
              </Box>
            </DialogContent>

            {/* Actions Section */}
            <Box sx={{ 
              p: 3, 
              borderTop: '1px solid',
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
              bgcolor: mode === 'dark' ? '#1F2129' : '#FFFFFF',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <Button 
                onClick={handleCreateReview} 
                variant="contained"
                disabled={submittingReview || reviewRating === 0}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  backgroundColor: '#4E67EB',
                  color: '#ffffff',
                  boxShadow: '0 2px 8px rgba(78, 103, 235, 0.25)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#3d56d9',
                    boxShadow: '0 4px 12px rgba(78, 103, 235, 0.35)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                  }
                }}
              >
                {submittingReview ? t('review.submitting', 'SUBMITTING...') : t('review.submit', 'SUBMIT REVIEW')}
              </Button>
            </Box>
          </Dialog>

          {/* Accept Change Proposal Dialog */}
          <Dialog 
            key={`accept-dialog-${i18n.language}`}
            open={acceptDialogOpen} 
            onClose={handleCloseAcceptDialog}
            maxWidth="sm"
            fullWidth
            transitionDuration={0}
          >
            <DialogTitle>{t('bookings.changeProposal.acceptTitle', 'Accept Change Proposal')}</DialogTitle>
            <DialogContent>
              <Typography key={`accept-message-${i18n.language}`} sx={{ mb: 2 }}>
                {selectedChangeProposal && (() => {
                  const allBookings = [...inProgressBookings, ...confirmedBookings, ...cancelledBookings, ...pastBookings];
                  const booking = allBookings.find(b => 
                    b.roomBookingId === selectedChangeProposal.bookingId && 
                    b.changeProposal?.id === selectedChangeProposal.changeId
                  );
                  const autoAccept = booking?.changeProposal?.autoAcceptRoomBooking || false;
                  
                  return autoAccept
                    ? t('bookings.changeProposal.acceptMessageWithAutoApprove', 'Are you sure you want to accept this change proposal? The booking will be updated with the new details and automatically approved.')
                    : t('bookings.changeProposal.acceptMessage', 'Are you sure you want to accept this change proposal? The booking will be updated with the new details.');
                })()}
              </Typography>
              {selectedChangeProposal && (() => {
                // Find the booking with this change proposal
                const allBookings = [...inProgressBookings, ...confirmedBookings, ...cancelledBookings, ...pastBookings];
                const booking = allBookings.find(b => 
                  b.roomBookingId === selectedChangeProposal.bookingId && 
                  b.changeProposal?.id === selectedChangeProposal.changeId
                );
                
                if (!booking || !booking.changeProposal) return null;
                
                const roomChanged = booking.changeProposal.newRoomId !== booking.changeProposal.oldRoomId;
                const timeChanged = proposalTimeChanged(booking.changeProposal);
                
                if (!roomChanged && !timeChanged) return null;
                
                return (
                  <Box 
                    sx={{ 
                      mt: 2,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(79, 95, 235, 0.08)' : 'rgba(79, 95, 235, 0.04)',
                      border: '1px solid',
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(79, 95, 235, 0.3)' : 'rgba(79, 95, 235, 0.2)',
                    }}
                  >
                    {roomChanged && booking.changeProposal.newRoomName && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {t('bookings.room', 'Room')}:
                        </Typography>
                        <Typography variant="body2">
                          <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: 1 }}>
                            {booking.roomName}
                          </Box>
                          <Box component="span" fontWeight={600}>
                            {booking.changeProposal.newRoomName}
                          </Box>
                        </Typography>
                      </Box>
                    )}
                    {timeChanged && (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {t('bookings.dateTime', 'Date & Time')}:
                        </Typography>
                        <Typography variant="body2">
                          <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: 1 }}>
                            {formatDateTimeWithDurationUTC(
                              booking.changeProposal.oldStartTime,
                              getProposalOldEndIso(booking.changeProposal, booking.startTime, booking.endTime),
                            )}
                          </Box>
                          <Box component="span" fontWeight={600}>
                            {formatDateTimeWithDurationUTC(
                              booking.changeProposal.newStartTime,
                              getChangeProposalNewEndIso(booking.changeProposal, booking.startTime, booking.endTime),
                            )}
                          </Box>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={handleCloseAcceptDialog} 
                disabled={processingChange !== null}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleAcceptChange} 
                color="success" 
                variant="contained"
                disabled={processingChange !== null}
                startIcon={processingChange ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {t('bookings.changeProposal.confirmAccept', 'Accept')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Decline Change Proposal Dialog */}
          <Dialog 
            key={`decline-dialog-${i18n.language}`}
            open={declineDialogOpen} 
            onClose={handleCloseDeclineDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>{t('bookings.changeProposal.declineTitle', 'Decline Change Proposal')}</DialogTitle>
            <DialogContent>
              <Typography sx={{ mb: 2 }}>
                {t('bookings.changeProposal.declineMessage', 'Are you sure you want to decline this change proposal? You can optionally provide a reason.')}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('bookings.changeProposal.declineReason', 'Reason (optional)')}
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder={t('bookings.changeProposal.declineReasonPlaceholder', 'Enter your reason for declining...')}
                variant="outlined"
              />
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={handleCloseDeclineDialog} 
                disabled={processingChange !== null}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleDeclineChange} 
                color="error" 
                variant="contained"
                disabled={processingChange !== null}
                startIcon={processingChange ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {t('bookings.changeProposal.confirmDecline', 'Decline')}
              </Button>
            </DialogActions>
          </Dialog>

        </Card>
      </Box>
    </>
  );
} 